import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Pencil, Trash2, CalendarIcon, DollarSign, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { inventory_id: '', brand: '', model: '', sale_price: '', purchase_price: '', buyer_name: '', sale_date: new Date(), notes: '' };

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [salesRes, invRes] = await Promise.all([
        axios.get(`${API}/sales`, { withCredentials: true }),
        axios.get(`${API}/inventory`, { withCredentials: true }),
      ]);
      setSales(salesRes.data);
      setInventory(invRes.data);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const inStockItems = inventory.filter(i => i.status === 'in_stock');

  const openAdd = () => {
    setEditingSale(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (sale) => {
    setEditingSale(sale);
    setForm({
      inventory_id: sale.inventory_id || '',
      brand: sale.brand, model: sale.model,
      sale_price: String(sale.sale_price), purchase_price: String(sale.purchase_price),
      buyer_name: sale.buyer_name || '', sale_date: sale.sale_date ? new Date(sale.sale_date) : new Date(),
      notes: sale.notes || ''
    });
    setDialogOpen(true);
  };

  const onInventorySelect = (invId) => {
    if (invId === 'none') {
      setForm({ ...form, inventory_id: '', brand: '', model: '', purchase_price: '' });
      return;
    }
    const item = inventory.find(i => i.id === invId);
    if (item) {
      setForm({ ...form, inventory_id: invId, brand: item.brand, model: item.model, purchase_price: String(item.purchase_price) });
    }
  };

  const handleSubmit = async () => {
    if (!form.brand || !form.model || !form.sale_price || !form.purchase_price) {
      toast.error('Brand, Model, Sale Price, and Purchase Price are required');
      return;
    }
    const payload = {
      inventory_id: form.inventory_id || null,
      brand: form.brand, model: form.model,
      sale_price: parseFloat(form.sale_price),
      purchase_price: parseFloat(form.purchase_price),
      buyer_name: form.buyer_name,
      sale_date: form.sale_date instanceof Date ? format(form.sale_date, 'yyyy-MM-dd') : form.sale_date,
      notes: form.notes,
    };
    try {
      if (editingSale) {
        await axios.put(`${API}/sales/${editingSale.id}`, payload, { withCredentials: true });
        toast.success('Sale updated');
      } else {
        await axios.post(`${API}/sales`, payload, { withCredentials: true });
        toast.success('Sale recorded');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to save sale');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/sales/${id}`, { withCredentials: true });
      toast.success('Sale deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete sale');
    }
  };

  const totalRevenue = sales.reduce((s, r) => s + (r.sale_price || 0), 0);
  const totalProfit = sales.reduce((s, r) => s + (r.profit || 0), 0);

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/export/sales/${format}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sales.${format === 'csv' ? 'csv' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Sales exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-foreground tracking-wide">SALES</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {sales.length} sales | Revenue: ${totalRevenue.toLocaleString()} | Profit: ${totalProfit.toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-sales-btn">
                <Download className="h-4 w-4" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-sales-csv">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-sales-pdf">
                <FileText className="h-4 w-4 mr-2" /> Export PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openAdd} data-testid="add-sale-btn" className="gap-2">
            <Plus className="h-4 w-4" /> Record Sale
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {sales.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-1">No sales yet</p>
              <p className="text-sm">Record your first sale to start tracking profits.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone</TableHead>
                  <TableHead className="text-right">Sale Price</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sales.map(sale => (
                  <TableRow key={sale.id} data-testid={`sale-row-${sale.id}`}>
                    <TableCell className="font-medium">{sale.brand} {sale.model}</TableCell>
                    <TableCell className="text-right">${sale.sale_price?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-muted-foreground">${sale.purchase_price?.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary" className={sale.profit >= 0 ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}>
                        {sale.profit >= 0 ? '+' : ''}${sale.profit?.toLocaleString()}
                      </Badge>
                    </TableCell>
                    <TableCell>{sale.buyer_name || '\u2014'}</TableCell>
                    <TableCell className="text-muted-foreground">{sale.sale_date?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`sale-actions-${sale.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(sale)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(sale.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="sale-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingSale ? 'Edit Sale' : 'Record Sale'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {!editingSale && inStockItems.length > 0 && (
              <div className="space-y-2">
                <Label>Link to Inventory (optional)</Label>
                <Select value={form.inventory_id || 'none'} onValueChange={onInventorySelect}>
                  <SelectTrigger data-testid="select-inventory"><SelectValue placeholder="Select a phone..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Manual Entry</SelectItem>
                    {inStockItems.map(item => (
                      <SelectItem key={item.id} value={item.id}>{item.brand} {item.model} (${item.purchase_price})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brand *</Label>
                <Input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} placeholder="e.g. Apple" data-testid="input-sale-brand" />
              </div>
              <div className="space-y-2">
                <Label>Model *</Label>
                <Input value={form.model} onChange={e => setForm({...form, model: e.target.value})} placeholder="e.g. iPhone 15" data-testid="input-sale-model" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sale Price *</Label>
                <Input type="number" value={form.sale_price} onChange={e => setForm({...form, sale_price: e.target.value})} placeholder="0.00" data-testid="input-sale-price" />
              </div>
              <div className="space-y-2">
                <Label>Purchase Price *</Label>
                <Input type="number" value={form.purchase_price} onChange={e => setForm({...form, purchase_price: e.target.value})} placeholder="0.00" data-testid="input-sale-purchase-price" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Buyer Name</Label>
                <Input value={form.buyer_name} onChange={e => setForm({...form, buyer_name: e.target.value})} placeholder="Optional" data-testid="input-buyer-name" />
              </div>
              <div className="space-y-2">
                <Label>Sale Date</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="sale-date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.sale_date instanceof Date ? format(form.sale_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.sale_date} onSelect={(d) => { if (d) { setForm({...form, sale_date: d}); setCalendarOpen(false); }}} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes..." data-testid="input-sale-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-sale-btn">Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-sale-btn">{editingSale ? 'Update' : 'Record Sale'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
